'use client'
import { useCallback } from 'react'

import { useUser } from '@auth0/nextjs-auth0'

import { useUserStore } from '@common/modules/user/stores'

import { useOnboarding } from '../stores'
import { isOnboardingAlreadyComplete } from '../utils/isOnboardingAlreadyComplete'
import { runOnboardingWorkspaceSetup } from '../utils/runOnboardingWorkspaceSetup'

export function useOnboardingFlow() {
  const { user: auth0User, isLoading: auth0Loading } = useUser()
  const { isOnboarding, currentStep, error, startOnboarding, setCurrentStep, completeOnboarding, setError } =
    useOnboarding()

  const ensureOnboardingComplete = useCallback(async () => {
    if (!auth0Loading && !auth0User) {
      return
    }

    if (isOnboardingAlreadyComplete()) {
      return
    }

    const userAfterInitialization = useUserStore.getState().currentUser
    if (!userAfterInitialization) {
      return
    }

    try {
      startOnboarding()
      setCurrentStep('workspace')

      try {
        await runOnboardingWorkspaceSetup()

        if (!isOnboardingAlreadyComplete()) {
          setCurrentStep('project')
        }
      } catch (setupError: Error | unknown) {
        console.error('[Onboarding] workspace setup failed:', setupError)
      }

      await new Promise((resolve) => setTimeout(resolve, 1500))

      setCurrentStep('complete')
      completeOnboarding()
    } catch (error: Error | unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Onboarding process failed.'
      setError(errorMessage)
      throw error
    }
  }, [auth0User, auth0Loading, startOnboarding, setCurrentStep, completeOnboarding, setError])

  return {
    error,
    currentStep,
    isOnboarding,
    ensureOnboardingComplete,
  }
}
